import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCreateItemVariant,
  useUpdateItemVariant,
  useListAttributeValues,
  getListItemVariantsQueryKey,
  getListInventoryItemsQueryKey,
} from "@workspace/api-client-react";
import type { InventoryItemVariant } from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { AttributeKeyCombobox } from "./attribute-key-combobox";
import { AttributeValueCombobox } from "./attribute-value-combobox";

interface VariantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  itemId: number;
  parentItemName: string;
  variant?: InventoryItemVariant;
}

export function VariantDialog({
  open,
  onOpenChange,
  mode,
  itemId,
  parentItemName,
  variant,
}: VariantDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const createMutation = useCreateItemVariant();
  const updateMutation = useUpdateItemVariant();

  // Track attribute key id separately (not part of Create/Update body directly)
  const [attributeKeyId, setAttributeKeyId] = useState<number | null>(null);
  const [attributeValueId, setAttributeValueId] = useState<number | null>(
    variant?.attributeValueId ?? null
  );

  // 2026-05-19 hot-fix: load ALL attribute values (no key filter) so we can
  // reverse-lookup the variant's attribute_key_id from its attribute_value_id.
  // Without this, Edit Variant opened with empty Attribute Key / Value selects.
  const { data: allValuesUnfiltered = [] } = useListAttributeValues();

  // When opening Edit for an existing variant, derive the attributeKeyId from
  // the variant's attribute_value_id so the dropdowns pre-populate.
  useEffect(() => {
    if (mode !== "edit") return;
    if (attributeKeyId != null) return;
    if (variant?.attributeValueId == null) return;
    const av = allValuesUnfiltered.find((v) => v.id === variant.attributeValueId);
    if (av) setAttributeKeyId(av.attributeKeyId);
  }, [mode, variant?.attributeValueId, allValuesUnfiltered, attributeKeyId]);
  const [itemCode, setItemCode] = useState(variant?.itemCode ?? "");
  const [currentStock, setCurrentStock] = useState(
    variant?.currentStock ?? "0"
  );
  // 2026-05-18: per-variant price override. "" = inherit parent, otherwise a numeric string.
  const [sellingPrice, setSellingPrice] = useState<string>(
    variant?.sellingPrice != null ? String(variant.sellingPrice) : ""
  );
  const [status, setStatus] = useState<"Active" | "Discontinued">(
    (variant?.status as "Active" | "Discontinued") ?? "Active"
  );

  // Lookup the attribute value label for the display name preview
  // attributeKeyId might be null on edit (we load it from the value's key lazily)
  const { data: allValues = [] } = useListAttributeValues(
    attributeKeyId != null ? { attributeKeyId, status: "Active" } : undefined,
    {
      query: {
        queryKey: ["listAttributeValues", attributeKeyId],
        enabled: attributeKeyId != null,
      },
    }
  );

  const selectedValueText =
    allValues.find((v) => v.id === attributeValueId)?.value ?? "";

  const displayNamePreview =
    selectedValueText
      ? `${parentItemName} — ${selectedValueText}`
      : `${parentItemName} — (pick a value)`;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (attributeValueId == null) {
      toast({
        title: "Attribute value is required",
        variant: "destructive",
      });
      return;
    }

    try {
      const sellingPriceValue = sellingPrice.trim() === "" ? null : sellingPrice;
      if (mode === "create") {
        await createMutation.mutateAsync({
          itemId,
          data: {
            attributeValueId,
            itemCode: itemCode.trim() || null,
            currentStock: currentStock || "0",
            sellingPrice: sellingPriceValue,
            status,
          },
        });
        toast({ title: "Variant created." });
      } else if (variant) {
        await updateMutation.mutateAsync({
          itemId,
          variantId: variant.id,
          data: {
            attributeValueId,
            itemCode: itemCode.trim() || null,
            currentStock: currentStock || "0",
            sellingPrice: sellingPriceValue,
            status,
          },
        });
        toast({ title: "Variant updated." });
      }

      queryClient.invalidateQueries({
        queryKey: getListItemVariantsQueryKey(itemId),
      });
      queryClient.invalidateQueries({
        queryKey: getListInventoryItemsQueryKey(),
      });
      onOpenChange(false);
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 409) {
        toast({
          title: "This attribute already has a variant on this item.",
          variant: "destructive",
        });
      } else if (status === 422) {
        toast({
          title: "Attribute value belongs to a different space.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Failed to save variant. Please try again.",
          variant: "destructive",
        });
      }
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Add Variant" : "Edit Variant"}
          </DialogTitle>
        </DialogHeader>

        {/* key-remount wrapper prevents stale form state when switching between variants */}
        <div key={variant?.id ?? "create"}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>
                Attribute Key <span className="text-destructive">*</span>
              </Label>
              <AttributeKeyCombobox
                value={attributeKeyId}
                onChange={(id) => {
                  setAttributeKeyId(id);
                  setAttributeValueId(null);
                }}
              />
            </div>

            <div className="space-y-2">
              <Label>
                Attribute Value <span className="text-destructive">*</span>
              </Label>
              <AttributeValueCombobox
                attributeKeyId={attributeKeyId}
                value={attributeValueId}
                onChange={setAttributeValueId}
                disabled={attributeKeyId == null}
              />
            </div>

            <div className="space-y-2">
              <Label>Item Code</Label>
              <Input
                placeholder="Optional per-variant SKU"
                value={itemCode}
                onChange={(e) => setItemCode(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>
                Current Stock <span className="text-destructive">*</span>
              </Label>
              <Input
                type="number"
                min="0"
                step="any"
                value={currentStock}
                onChange={(e) => setCurrentStock(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>
                Variant Price{" "}
                <span className="text-xs text-muted-foreground">(optional — overrides parent)</span>
              </Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="Inherits parent item.sellingPrice"
                value={sellingPrice}
                onChange={(e) => setSellingPrice(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={status}
                onValueChange={(v) =>
                  setStatus(v as "Active" | "Discontinued")
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Discontinued">Discontinued</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Display name preview: </span>
              {displayNamePreview}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Saving…" : "Save Variant"}
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
